<template>
  <main class="mega-all-products bg-gray-50 min-h-screen">
    <div class="container mx-auto px-4 py-12">
      <!-- Combined Search and Filter Card -->
      <div class="mega-search-filter-card bg-white rounded-xl shadow-lg p-6 mb-8 sticky top-4 z-30">
        <!-- Search Bar -->
        <div class="flex items-center gap-4 mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16l-4-4m0 0l4-4m-4 4h16" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search products..."
            class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
          />
          <button
            @click="searchQuery = ''"
            class="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Clear search"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Category Filter -->
        <div>
          <h2 class="text-lg font-semibold text-gray-700 mb-4">
            {{ $t('productsSection.filterTitle') }}
          </h2>

          <div class="flex flex-wrap gap-3">
            <button
              @click="selectCategory('')"
              :class="[
                'mega-filter-btn px-5 py-2.5 rounded-lg transition-all duration-300 flex items-center',
                !selectedCategory ? 'bg-green-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              ]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              {{ $t("categories.toutCategory") }}
            </button>

            <button
              v-for="category in categories"
              :key="category.id"
              @click="selectCategory(category.id)"
              :class="[
                'mega-filter-btn px-5 py-2.5 rounded-lg transition-all duration-300 flex items-center',
                selectedCategory === category.id ? 'bg-green-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              ]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              {{ category.name }}
            </button>
          </div>
        </div>
      </div>

      <!-- Debug Info
      <div class="bg-yellow-50 p-4 rounded-lg mb-6" v-if="showDebug">
        <p>Products count: {{ products.length }}</p>
        <p>Categories count: {{ categories.length }}</p>
        <p>Selected category: {{ selectedCategory }}</p>
        <p>Filtered products count: {{ filteredProducts.length }}</p>
        <p>Loading: {{ loading }}</p>
        <p v-if="error">Error: {{ error }}</p>
        <button @click="loadData" class="mt-2 px-4 py-2 bg-green-500 text-white rounded">Retry</button>
      </div> -->

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center items-center min-h-[50vh]">
        <div>Loading...</div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="text-center py-20 text-red-600">
        {{ error }}
      </div>

      <!-- Products Grid -->
      <TransitionGroup
        v-else-if="filteredProducts.length"
        name="mega-product-grid"
        tag="div"
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        <div
          v-for="product in filteredProducts"
          :key="product.id"
          class="mega-product-card bg-white rounded-2xl shadow-xl overflow-hidden group relative border border-white/20"
        >
          <div class="relative overflow-hidden aspect-w-4 aspect-h-3">
            <img
              v-if="product.img"
              :src="getImageUrl(product.img)"
              :alt="product.name"
              class="w-full h-80 object-contain transform transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          </div>

          <div class="p-6 space-y-4">
            <h3 class="text-xl font-bold text-gray-800">{{ product.name }}</h3>
            <p class="text-gray-600 line-clamp-2">{{ product.description }}</p>
            <div class="text-sm text-gray-700" v-if="product.Largeur && product.Hauteur && product.Profondeur">
              {{ product.Largeur }} (L) × {{ product.Hauteur }} (H) × {{ product.Profondeur }} (P) mm
            </div>
            <RouterLink :to="`/detailproduct/${product.id}`" class="block">
              <button class="w-full px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700">
                {{ $t('productsSection.buttonText') }}
              </button>
            </RouterLink>
          </div>
        </div>
      </TransitionGroup>

      <!-- No Products -->
      <div v-else class="text-center py-20">
        <h3>{{ $t('allProducts.noProductsTitle') }}</h3>
        <p>{{ $t('allProducts.noProductsMessage') }}</p>
        <button @click="resetFilters" class="mt-6 px-6 py-2 bg-green-600 text-white rounded-lg">
          {{ $t('allProducts.resetFilters') }}
        </button>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { apiService } from '@/services/apiService';
import { buildStorageUrl } from '@/apiConfig';

const products = ref([]);
const categories = ref([]);
const loading = ref(false);
const error = ref(null);
const selectedCategory = ref(null);
const searchQuery = ref(''); // New reactive variable for search query
const showDebug = ref(true); // Set to false in production

const getImageUrl = (imgPath) => {
  if (!imgPath) return '';
  return buildStorageUrl(imgPath);
};

const loadData = async () => {
  loading.value = true;
  error.value = null;
  try {
    // Fetch products and categories in parallel
    const [productsResponse, categoriesResponse] = await Promise.all([
      apiService.getProducts(),
      apiService.getCategories()
    ]);

    // Handle products response
    if (productsResponse.data && Array.isArray(productsResponse.data.data)) {
      products.value = productsResponse.data.data;
    } else if (Array.isArray(productsResponse.data)) {
      products.value = productsResponse.data;
    } else {
      products.value = [];
    }

    // Handle categories response
    if (categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
      categories.value = categoriesResponse.data;
    } else {
      categories.value = [];
    }

    console.log('Products loaded:', products.value);
    console.log('Categories loaded:', categories.value);

  } catch (err) {
    error.value = err.message || 'Failed to load data.';
    console.error('Error loading data:', err);
  } finally {
    loading.value = false;
  }
};

const filteredProducts = computed(() => {
  let filtered = products.value;

  // Filter by category
  if (selectedCategory.value) {
    filtered = filtered.filter(product => 
      product.category_id === selectedCategory.value
    );
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(product => 
      product.name.toLowerCase().includes(query) ||
      product.description.toLowerCase().includes(query)
    );
  }

  return filtered;
});

const selectCategory = (id) => {
  selectedCategory.value = id || null;
};

const resetFilters = () => {
  selectedCategory.value = null;
  searchQuery.value = ''; // Reset search query
};

onMounted(loadData);
</script>


<style scoped>
/* Hero Section */
.mega-products-hero {
  background-image: radial-gradient(circle at 75% 50%, rgba(0, 119, 255, 0.2) 0%, transparent 50%);
}

.mega-hero-title {
  position: relative;
  display: inline-block;
}

.mega-hero-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #FFD700, #FFFFFF);
  border-radius: 2px;
}

/* Combined Search and Filter Card */
.mega-search-filter-card {
  padding: 1.5rem;
  margin-top: 1.5rem;
}

.mega-search-filter-card .mega-filter-btn {
  transition: all 0.3s ease;
}

.mega-search-filter-card .mega-filter-btn:hover {
  transform: translateY(-2px);
}

/* Top Bar Search */
.mega-search-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.mega-search-bar input {
  flex: 1;
  margin-top: 1rem; /* Corrected the incomplete 'margin' property */
}

.mega-search-bar button {
  background: none;
  border: none;
  cursor: pointer;
}

.mega-search-bar button:hover {
  color: #1d4ed8;
}

/* Category Filter */
.mega-category-filter {
  margin-top: 1rem;
}

/* Product Card */
.mega-product-card {
  backdrop-filter: blur(5px);
  transition: transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275),
              box-shadow 0.5s ease;
}

.mega-product-card:hover {
  box-shadow: 0 25px 50px -12px rgba(0, 119, 255, 0.25);
}

.mega-product-image-container {
  transition: all 0.7s cubic-bezier(0.19, 1, 0.22, 1);
}

.mega-product-overlay {
  background-image: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, transparent 50%);
}

.mega-product-name {
  position: relative;
}

.mega-product-name::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 0;
  height: 2px;
  background: #3b82f6;
  transition: width 0.3s ease;
}

.mega-product-card:hover .mega-product-name::after {
  width: 50px;
}

.mega-product-desc {
  transition: color 0.3s ease;
}

.mega-product-button {
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
  transition: all 0.3s ease, box-shadow 0.3s ease;
}

.mega-product-button:hover {
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.6);
}

/* Spinner Animation */
.mega-spinner {
  --uib-size: 60px;
  --uib-speed: 1.4s;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--uib-size);
  height: var(--uib-size);
}

.mega-spinner-dot {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.mega-spinner-dot::before {
  content: '';
  display: block;
  width: 20%;
  height: 20%;
  border-radius: 50%;
  animation: mega-spin var(--uib-speed) linear infinite;
}

.mega-spinner-dot:nth-child(1)::before {
  animation-delay: calc(var(--uib-speed) * -0.45);
}

.mega-spinner-dot:nth-child(2)::before {
  animation-delay: calc(var(--uib-speed) * -0.3);
}

.mega-spinner-dot:nth-child(3)::before {
  animation-delay: calc(var(--uib-speed) * -0.15);
}

@keyframes mega-spin {
  0% {
    transform: scale(0);
    opacity: 0.5;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
}

/* Transition Animations */
.mega-product-grid-enter-active,
.mega-product-grid-leave-active {
  transition: all 0.8s cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.mega-product-grid-enter-from,
.mega-product-grid-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.mega-product-grid-move {
  transition: transform 0.8s ease;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .mega-hero-title {
    font-size: 3rem;
  }
}

@media (max-width: 768px) {
  .mega-hero-title {
    font-size: 2.5rem;
  }

  .mega-product-card {
    max-width: 100%;
  }

  .mega-search-bar {
    flex-direction: column;
    gap: 0.5rem;
  }

  .mega-category-filter {
    overflow-x: auto;
    white-space: nowrap;
  }

  .mega-search-filter-card {
    flex-direction: column;
    gap: 1rem;
  }
}

@media (max-width: 640px) {
  .mega-products-hero {
    padding-top: 6rem;
    padding-bottom: 6rem;
  }

  .mega-hero-title {
    font-size: 2rem;
  }
}
</style>
